import { useState, useCallback } from 'react';

interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  speaker?: string;
  speakerLabel?: string;
}

interface TranscriptCache {
  segments: TranscriptSegment[];
  speakerLabels: Record<number, string>;
  loadedAt: number;
}

// Global cache to persist transcript data across re-renders and component instances
const transcriptCacheMap = new Map<string, TranscriptCache>();

export const useTranscriptCache = (widgetId: string) => {
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>(() => {
    const cached = transcriptCacheMap.get(widgetId);
    return cached?.segments || [];
  });
  
  const [speakerLabels, setSpeakerLabels] = useState<Record<number, string>>(() => {
    const cached = transcriptCacheMap.get(widgetId);
    return cached?.speakerLabels || {};
  });
  
  const cacheTranscript = useCallback((segments: TranscriptSegment[], labels: Record<number, string>) => {
    transcriptCacheMap.set(widgetId, {
      segments,
      speakerLabels: labels,
      loadedAt: Date.now()
    });
    setTranscriptSegments(segments);
    setSpeakerLabels(labels);
  }, [widgetId]);
  
  const hasCache = useCallback(() => {
    return transcriptCacheMap.has(widgetId);
  }, [widgetId]);
  
  const clearCache = useCallback(() => {
    transcriptCacheMap.delete(widgetId);
    setTranscriptSegments([]);
    setSpeakerLabels({});
  }, [widgetId]);
  
  return {
    transcriptSegments,
    speakerLabels,
    cacheTranscript,
    hasCache,
    clearCache
  };
};